/**
 * Organisationslagret: avtalsparten ovanför användaren.
 * Här hanteras medlemmar (licenser), avtalspaket, förbrukning och
 * begäran om höjt tak. Fakturering sker manuellt – systemet levererar underlaget.
 */
import { supabase } from "@/integrations/supabase/client";

export type OrgRole = "owner" | "admin" | "member" | "viewer";

export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Ägare",
  admin: "Administratör",
  member: "Medlem",
  viewer: "Läsare",
};

export interface Organization {
  id: string;
  name: string;
  org_no: string | null;
  billing_email: string | null;
  billing_address: string | null;
  billing_reference: string | null;
  status: string;
  is_personal: boolean;
  created_at: string;
}

export interface OrgMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
  is_active: boolean;
  activated_at: string;
  deactivated_at: string | null;
  created_at: string;
  displayName?: string;
  company?: string | null;
}

export interface OrgPlan {
  id: string;
  organization_id: string;
  plan_name: string;
  seat_limit: number;
  project_limit: number;
  package_price: number;
  extra_seat_price: number;
  extra_project_price: number;
  currency: string;
  valid_from: string;
  valid_to: string | null;
}

export interface OrgEntitlements {
  organization_id: string;
  organization_name: string;
  plan_name: string;
  seat_limit: number;
  project_limit: number;
  active_seats: number;
  active_projects: number;
  extra_seats: number;
  extra_projects: number;
  package_price: number;
  extra_seat_price: number;
  extra_project_price: number;
  estimated_amount: number;
  currency: string;
  user_role: OrgRole | null;
}

export interface UsagePeriod {
  id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  active_seats: number;
  active_projects: number;
  seat_limit: number;
  project_limit: number;
  extra_seats: number;
  extra_projects: number;
  package_price: number;
  extra_seat_price: number;
  extra_project_price: number;
  total_amount: number;
  currency: string;
  invoiced: boolean;
}

export interface SeatRequest {
  id: string;
  organization_id: string;
  requested_by: string;
  requested_seat_limit: number | null;
  requested_project_limit: number | null;
  motivation: string | null;
  status: "pending" | "approved" | "rejected";
  decision_note: string | null;
  decided_at: string | null;
  created_at: string;
}

export const formatMoney = (value: number, currency = "SEK") =>
  `${Math.round(value).toLocaleString("sv-SE")} ${currency}`;

/** Användarens aktiva organisation (första aktiva medlemskapet). */
export async function getMyMembership(
  userId: string,
): Promise<{ membership: OrgMember; organization: Organization } | null> {
  const { data, error } = await supabase
    .from("organization_members")
    .select("*, organizations(*)")
    .eq("user_id", userId)
    .eq("is_active", true)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const { organizations, ...membership } = data as any;
  return {
    membership: membership as OrgMember,
    organization: organizations as Organization,
  };
}

/** Skapar en personlig organisation om användaren saknar medlemskap. */
export async function ensurePersonalOrganization(
  userId: string,
  displayName?: string | null,
): Promise<{ membership: OrgMember; organization: Organization }> {
  const existing = await getMyMembership(userId);
  if (existing) return existing;

  const { data: org, error: orgError } = await supabase
    .from("organizations")
    .insert({
      name: displayName?.trim() || "Personligt konto",
      is_personal: true,
      created_by: userId,
    })
    .select()
    .single();
  if (orgError) throw orgError;

  const { data: member, error: memberError } = await supabase
    .from("organization_members")
    .insert({ organization_id: org.id, user_id: userId, role: "owner" })
    .select()
    .single();
  if (memberError) throw memberError;

  return {
    membership: member as OrgMember,
    organization: org as Organization,
  };
}

export async function getEntitlements(orgId?: string): Promise<OrgEntitlements | null> {
  const { data, error } = await supabase.rpc("get_org_entitlements", {
    p_org: orgId ?? undefined,
  } as any);
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as OrgEntitlements) ?? null;
}

export async function getActivePlan(orgId: string): Promise<OrgPlan | null> {
  const { data, error } = await supabase
    .from("organization_plans")
    .select("*")
    .eq("organization_id", orgId)
    .order("valid_from", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as OrgPlan) ?? null;
}

export async function listMembers(orgId: string): Promise<OrgMember[]> {
  const { data, error } = await supabase
    .from("organization_members")
    .select("*")
    .eq("organization_id", orgId)
    .order("created_at", { ascending: true });
  if (error) throw error;

  const members = (data ?? []) as OrgMember[];

  // Namn hämtas via den säkra vyn så att e-post aldrig exponeras i listan.
  const enriched = await Promise.all(
    members.map(async (m) => {
      try {
        const { data: profile } = await supabase.rpc("get_safe_user_display_name", {
          user_id: m.user_id,
        });
        const row = Array.isArray(profile) ? profile[0] : profile;
        return { ...m, displayName: (row as any)?.display_name ?? "Användare" };
      } catch {
        return { ...m, displayName: "Användare" };
      }
    }),
  );
  return enriched;
}

export async function setMemberActive(memberId: string, isActive: boolean) {
  const { error } = await supabase
    .from("organization_members")
    .update({
      is_active: isActive,
      deactivated_at: isActive ? null : new Date().toISOString(),
    })
    .eq("id", memberId);
  if (error) throw error;
}

export async function setMemberRole(memberId: string, role: OrgRole) {
  const { error } = await supabase
    .from("organization_members")
    .update({ role })
    .eq("id", memberId);
  if (error) throw error;
}

export async function updateOrganization(orgId: string, patch: Partial<Organization>) {
  const { error } = await supabase.from("organizations").update(patch).eq("id", orgId);
  if (error) throw error;
}

export async function requestSeatIncrease(input: {
  organizationId: string;
  userId: string;
  seatLimit?: number | null;
  projectLimit?: number | null;
  motivation?: string;
}) {
  const { error } = await supabase.from("seat_increase_requests").insert({
    organization_id: input.organizationId,
    requested_by: input.userId,
    requested_seat_limit: input.seatLimit ?? null,
    requested_project_limit: input.projectLimit ?? null,
    motivation: input.motivation ?? null,
  });
  if (error) throw error;
}

export async function listSeatRequests(orgId?: string): Promise<SeatRequest[]> {
  let query = supabase
    .from("seat_increase_requests")
    .select("*")
    .order("created_at", { ascending: false });
  if (orgId) query = query.eq("organization_id", orgId);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as SeatRequest[];
}

export async function listUsagePeriods(orgId: string): Promise<UsagePeriod[]> {
  const { data, error } = await supabase
    .from("organization_usage_periods")
    .select("*")
    .eq("organization_id", orgId)
    .order("period_start", { ascending: false });
  if (error) throw error;
  return (data ?? []) as UsagePeriod[];
}
